import createCategorySearchQueryString from './createCategorySearchQueryString';
import createPersonaSearchQueryString from './createPersonaSearchQueryString';
import createPrivateDistributionListSearchQueryString from './createPrivateDistributionListSearchQueryString';
import getIsSearchingWithinFromFolder from './getIsSearchingWithinFromFolder';
import { AND_SEPARATOR, OR_SEPARATOR, SPACE } from '../constants';
import mailSearchStore from '../store/store';
import { LazySearchScenarioStore } from 'owa-search-next-lazy';
import { SubstrateSearchSuggestionSegment, SuggestionKind } from 'owa-search-service';

export default function getSearchQueryString(searchStore: LazySearchScenarioStore): string {
    // Create query strings for suggestion pills.
    const queryStringsForSuggestionPills: string[] = [];
    const toPeoplePillsQueryStringParts: string[] = [];
    const fromPeoplePillsQueryStringParts: string[] = [];
    for (let i = 0; i < searchStore.suggestionPillIds.length; i++) {
        const suggestion = searchStore.suggestionPills.get(searchStore.suggestionPillIds[i]);
        switch (suggestion.kind) {
            case SuggestionKind.People:
                if (suggestion.CustomQueryText) {
                    if (suggestion.CustomQueryText.FromKql) {
                        fromPeoplePillsQueryStringParts.push(suggestion.CustomQueryText.FromKql);
                    }

                    if (suggestion.CustomQueryText.ToKql) {
                        toPeoplePillsQueryStringParts.push(suggestion.CustomQueryText.ToKql);
                    }
                } else if (suggestion.QueryText) {
                    queryStringsForSuggestionPills.push(suggestion.QueryText);
                } else if (getIsSearchingWithinFromFolder(mailSearchStore.staticSearchScope)) {
                    fromPeoplePillsQueryStringParts.push(
                        createPersonaSearchQueryString(suggestion, true /* useFromPrefix */)
                    );
                } else {
                    toPeoplePillsQueryStringParts.push(
                        createPersonaSearchQueryString(suggestion, false /* useFromPrefix */)
                    );
                }
                break;

            case SuggestionKind.PrivateDistributionList:
                fromPeoplePillsQueryStringParts.push(
                    createPrivateDistributionListSearchQueryString(
                        suggestion,
                        mailSearchStore.staticSearchScope
                    )
                );
                break;

            case SuggestionKind.Category:
                queryStringsForSuggestionPills.push(createCategorySearchQueryString(suggestion));
                break;
        }
    }

    /**
     * To create KQL that makes sense with FROM and TO expressions for people
     * pills, we OR the parts within each expression, and AND the FROM and TO
     * expressions together.
     */
    const peoplePillsQueryStringParts: string[] = [];
    if (toPeoplePillsQueryStringParts.length > 0) {
        peoplePillsQueryStringParts.push(`(${toPeoplePillsQueryStringParts.join(OR_SEPARATOR)})`);
    }
    if (fromPeoplePillsQueryStringParts.length > 0) {
        peoplePillsQueryStringParts.push(`(${fromPeoplePillsQueryStringParts.join(OR_SEPARATOR)})`);
    }

    // Only add people pill parts if any exist.
    if (peoplePillsQueryStringParts.length > 0) {
        queryStringsForSuggestionPills.push(peoplePillsQueryStringParts.join(AND_SEPARATOR));
    }

    // Join pill queries into a single query (OR'ed).
    let combinedQueryStringForSearchPills = '';
    if (queryStringsForSuggestionPills.length > 0) {
        combinedQueryStringForSearchPills = queryStringsForSuggestionPills.join(OR_SEPARATOR);
    }

    // Join suggestion segments into a single query (AND'ed).
    let combinedQueryStringForSuggestionSegments = '';
    if (searchStore.suggestionSegments.size > 0) {
        combinedQueryStringForSuggestionSegments = [...searchStore.suggestionSegments.values()]
            .map((segment: SubstrateSearchSuggestionSegment) => segment.QueryText)
            .join(AND_SEPARATOR);
    }

    // Join pills query string with the search keyword query string.
    const queryString = [
        combinedQueryStringForSearchPills,
        combinedQueryStringForSuggestionSegments,
        searchStore.searchText,
    ]
        // Remove extra whitespace from parts.
        .map((query: string) => query && query.trim())
        // Remove any null or empty parts.
        .filter((query: string) => query.length > 0)
        // Concatenate parts with spaces in between.
        .join(SPACE);

    // Return KQL.
    return queryString;
}
